import { HttpClient, HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { deserializer } from '@shared/helpers/deserializer.helper';
import { errorHandler } from '@shared/helpers/handle-error.helper';
import { IPaginable } from '@shared/interfaces/paginable.interface';
import { IQRCode } from '@shared/interfaces/qrcode.interface';
import { QRCode } from '@shared/models/qrcode.model';
import { QRCodeSerializer } from '@shared/serializers/qrcode.serializer';
import { CommonEnvironmentsService } from '@shared/services/environments.service';
import { Observable } from 'rxjs';
import { catchError, map, tap } from 'rxjs/operators';

@Injectable({
  providedIn: 'root'
})
export class QRCodesService implements IPaginable {
  private readonly endpoint: string = String(
    `${this._commonEnvironments.API_ENDPOINT}/qrcodes`
  );

  constructor(
    private readonly _commonEnvironments: CommonEnvironmentsService,
    private readonly _http: HttpClient
  ) {}

  public delete(id: string): Observable<IQRCode> {
    return this._http.delete(`${this.endpoint}/${id}`).pipe(
      map((res: any) => new QRCode(deserializer.deserialize(res))),
      catchError(errorHandler)
    );
  }

  public find(id: string): Observable<IQRCode> {
    return this._http.get(`${this.endpoint}/${id}`).pipe(
      map((res: any) => new QRCode(deserializer.deserialize(res))),
      catchError(errorHandler)
    );
  }

  public get(
    pageNumber?: number,
    filters?: any,
    onGetRawData?: (data: any) => void
  ): Observable<IQRCode[]> {
    let httpParams = new HttpParams();

    if (pageNumber) {
      httpParams = httpParams.set('page[number]', String(pageNumber));
    }

    if (filters) {
      filters.map((f) => {
        if (f.value) {
          httpParams = httpParams.append(f.key, f.value);
        }
      });
    }

    return this._http.get(this.endpoint, { params: httpParams }).pipe(
      tap((data: any) => onGetRawData && onGetRawData(data)),
      map((res: any) =>
        deserializer.deserialize(res).map((qr: IQRCode) => new QRCode(qr))
      ),
      catchError(errorHandler)
    );
  }

  public patch(q: Partial<IQRCode>): Observable<IQRCode> {
    const data = QRCodeSerializer.serialize(q);
    return this._http.patch(`${this.endpoint}/${q.id}`, data).pipe(
      map((res: any) => new QRCode(deserializer.deserialize(res))),
      catchError(errorHandler)
    );
  }

  public post(q: Partial<IQRCode>): Observable<IQRCode> {
    const data = QRCodeSerializer.serialize(q);
    return this._http.post(this.endpoint, data).pipe(
      map((res: any) => new QRCode(deserializer.deserialize(res))),
      catchError(errorHandler)
    );
  }
}
